import { useState, useEffect, useCallback } from 'react';
import { reportService } from '../services/report';
import { Option } from '../types/report.types';

interface UseReportDataOptions {
  autoFetch?: boolean;
}

interface ReportDataState {
  departments: Option[];
  employees: Option[];
  locations: Option[];
  roles: Option[];
  loading: {
    departments: boolean;
    employees: boolean;
    locations: boolean;
    roles: boolean;
  };
  errors: {
    departments: string | null;
    employees: string | null;
    locations: string | null;
    roles: string | null;
  };
}

export const useReportData = (options: UseReportDataOptions = {}) => {
  const { autoFetch = true } = options;
  
  const [state, setState] = useState<ReportDataState>({
    departments: [{ id: 'all', name: 'All Departments' }],
    employees: [{ id: 'all', name: 'All Employees' }],
    locations: [{ id: 'all', name: 'All Locations' }],
    roles: [{ id: 'all', name: 'All Roles' }],
    loading: {
      departments: false,
      employees: false,
      locations: false,
      roles: false,
    },
    errors: {
      departments: null,
      employees: null,
      locations: null,
      roles: null,
    },
  });

  const updateLoading = (key: keyof ReportDataState['loading'], value: boolean) => {
    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, [key]: value }
    }));
  };

  const updateError = (key: keyof ReportDataState['errors'], value: string | null) => {
    setState(prev => ({
      ...prev,
      errors: { ...prev.errors, [key]: value }
    }));
  };

  const fetchDepartments = useCallback(async () => {
    updateLoading('departments', true);
    updateError('departments', null);
    
    try {
      const response = await reportService.getDepartments();
      if (response.status === 'GET_DEPARTMENTS_SUCCESS' && response.payload?.departments) {
        setState(prev => ({
          ...prev,
          departments: [
            { id: 'all', name: 'All Departments' },
            ...response.payload.departments
          ]
        }));
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      updateError('departments', 'Failed to load departments');
    } finally {
      updateLoading('departments', false);
    }
  }, []);

  const fetchEmployees = useCallback(async (departmentId?: string) => {
    updateLoading('employees', true);
    updateError('employees', null);
    
    try {
      const response = await reportService.getEmployees(departmentId);
      if (response.status === 'GET_EMPLOYEES_SUCCESS' && response.payload?.employees) {
        setState(prev => ({
          ...prev,
          employees: [
            { id: 'all', name: 'All Employees' },
            ...response.payload.employees
          ]
        }));
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      updateError('employees', 'Failed to load employees');
    } finally {
      updateLoading('employees', false);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    updateLoading('locations', true);
    updateError('locations', null);
    
    try {
      const response = await reportService.getLocations();
      if (response.status === 'GET_LOCATIONS_SUCCESS' && response.payload?.locations) {
        setState(prev => ({
          ...prev,
          locations: [
            { id: 'all', name: 'All Locations' },
            ...response.payload.locations
          ]
        }));
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      updateError('locations', 'Failed to load locations');
    } finally {
      updateLoading('locations', false);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    updateLoading('roles', true);
    updateError('roles', null);
    
    try {
      const response = await reportService.getRoles();
      if (response.status === 'GET_ROLES_SUCCESS' && response.payload?.roles) {
        setState(prev => ({
          ...prev,
          roles: [
            { id: 'all', name: 'All Roles' },
            ...response.payload.roles
          ]
        }));
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      updateError('roles', 'Failed to load roles');
    } finally {
      updateLoading('roles', false);
    }
  }, []);

  const fetchAllOptions = useCallback(async () => {
    await Promise.all([
      fetchDepartments(),
      fetchLocations(),
      fetchRoles()
    ]);
  }, [fetchDepartments, fetchLocations, fetchRoles]);

  // Auto-fetch data on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchAllOptions();
    }
  }, [autoFetch, fetchAllOptions]);

  return {
    ...state,
    refetch: {
      departments: fetchDepartments,
      employees: fetchEmployees,
      locations: fetchLocations,
      roles: fetchRoles,
      all: fetchAllOptions,
    },
  };
};

export default useReportData;